import useHasChanged from "Hooks/useHasChanged";
import { observer, useLocalStore } from "mobx-react";
import { OrganisationEntity } from "Models/Entities";
import { accountType } from "Models/Enums";
import { organisationAccountTypeComboboxOptions, organisationCategoryComboboxOptions } from "Models/Enums/MappedEnumOptions";
import moment from "moment";
import alertToast from "Util/ToastifyUtils";
import { Colors, Display, Sizes } from "../Button/Button";
import ButtonAsyncState from "../Button/ButtonAsyncState";
import { Combobox } from "../Combobox/Combobox";
import { DatePicker } from "../DatePicker/DatePicker";
import { confirmModalAsync, IConfirmModalOptions } from "../Modal/ModalUtils";

const confirmModalOptions: IConfirmModalOptions = {
	shouldCloseOnEsc: false,
	shouldCloseOnOverlayClick: false,
};

function ConfirmChangedOrgAccountTypeModalContent() {
	return (
		<p>
			Please make sure you understand the implications of this change before proceeding. <br />
			<ul>
				<li>All monitoring expiry date will be cleared from customers.</li>
			</ul>
			<p className="alert alert__warning">This change is irreversible.</p>
		</p>
	);
}

type Props = {
	organisation: OrganisationEntity;
}

export const OrganisationConfiguration: React.FunctionComponent<Props> = observer(({ organisation: initialOrg }) => {
	const organisation = useLocalStore(() => (new OrganisationEntity(initialOrg)));

	const chooseOrganisationToolTip = "Choose how this organisation should be billed:"
		+ "\n\nCredit Card -  Charges are deducted automatically as you use the service. A payment method is required upfront."
		+ "\n\nInvoice - Receive a bill for usage at the end of each billing cycle. Payment is due based on agreed terms.";

	const hasChanged = useHasChanged(
		OrganisationEntity,
		organisation,
		["accountType", "organisationCategory", "freeTrialExpiryDate", "bsb"],
		(oldValue, newValue) => {
			if (oldValue instanceof Date && newValue instanceof Date) {
				const oldDate = moment(oldValue).format("YYYY-MM-DD");
				const newDate = moment(newValue).format("YYYY-MM-DD");
				return oldDate !== newDate;
			}
			return oldValue !== newValue
		},
		['errors'],
		(original, changed) => Object.keys(changed.errors).length > 0,
	);

	const onAfterChangeOrgAccountType = (value: accountType) => {
		if (value === "PAYG") organisation.freeTrialExpiryDate = null;
		organisation.validateField("accountType");
	}

	const saveChanges = async () => {
		// check if the account type has changed
		const hasAccountTypeChanged = initialOrg.accountType !== organisation.accountType;
		if (hasAccountTypeChanged) {
			const modal = confirmModalAsync(
				"Are you sure?",
				<ConfirmChangedOrgAccountTypeModalContent />,
				confirmModalOptions,
			);

			try {
				await modal.result();
			} catch (error) {
				return;
			}
			modal.close();
		}

		try {
			await organisation.save();
			alertToast("Successfully updated the organisation", "success");
		} catch (error) {
			alertToast("Failed to update the organisation", "error");
			return;
		}
	}

	return (
		<div className="organisation-configuration">
			<div className="organisation-configuration__header">
				<h2>Configuration</h2>
			</div>
			<div className="organisation-configuration__content">
				<Combobox
					label="Choose Organisation Size"
					model={organisation}
					modelProperty="organisationCategory"
					placeholder="Select Organisation Size"
					options={organisationCategoryComboboxOptions}
					searchable
					getOptionValue={item => item}
					onAfterChange={() => organisation.validateField("organisationCategory")}
					errors={organisation.errors.organisationCategory}
				/>
				<Combobox
					label="Choose Organisation Account Type"
					model={organisation}
					modelProperty="accountType"
					placeholder="Select Organisation Account Type"
					options={organisationAccountTypeComboboxOptions}
					searchable
					getOptionValue={item => item}
					onAfterChange={(_, data) => onAfterChangeOrgAccountType(data.value as accountType)}
					isDisabled={initialOrg.accountType === "INVOICED"}
					errors={organisation.errors.accountType}
					tooltip={chooseOrganisationToolTip}
				/>
				<DatePicker
					model={organisation}
					modelProperty="freeTrialExpiryDate"
					label="Trial End Date"
					onAfterChange={(_, date) => {
						if (date === "") {
							organisation.freeTrialExpiryDate = null;
						} else {
							organisation.freeTrialExpiryDate = moment(date).endOf('day').toDate();
							organisation.validateField("freeTrialExpiryDate");
						}
					}}
					errors={organisation.errors.freeTrialExpiryDate}
					isDisabled={organisation.accountType === "PAYG"}
					tooltip={organisation.accountType === "PAYG" ? "You can only put an organisation that is invoiced on trial." : ""}
					clickToClear
				/>
			</div>
			<div className="organisation-configuration__footer">
				<ButtonAsyncState
					display={Display.Solid}
					colors={Colors.Primary}
					sizes={Sizes.Medium}
					onPress={saveChanges}
					waitingText="Saving..."
					readonly={hasChanged}
				>
					Save
				</ButtonAsyncState>
			</div>
		</div>
	)
});
